import { useMutation } from '@apollo/client'
import { useEffect, useState } from 'react'

import {
  ArtistUpdateArtistProfile,
  ArtistUpdateArtistProfileMutation,
  ArtistUpdateArtistProfileMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import {
  useCurrentUser,
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useAuth } from '@/core/auth'
import { Camera } from '@/core/native'
import {
  getInputSignUpArtist,
  getRedirectAfterLoginPath,
  removeInputSignUpArtist,
} from '@/core/native/storage'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { getUserClaims, reloadUser } = useAuth()
  const { routerPages, router } = useRouter()
  const [hiddenDisplayName, setHiddenDisplayName] = useState<boolean>(false)
  const [hiddenAccountId, setHiddenAccountId] = useState<boolean>(false)
  const [hiddenInviteCode, setHiddenInvitationCode] = useState<boolean>(false)

  const { user } = useCurrentUser()
  const { t } = useTranslation()
  const { showError } = useMessage()
  const { getImage } = Camera
  const { upload } = useUserTmpUploader(user?.uid ?? '')

  const [artistUpdateArtistProfile] = useMutation<
    ArtistUpdateArtistProfileMutation,
    ArtistUpdateArtistProfileMutationVariables
  >(ArtistUpdateArtistProfile)

  useEffect(() => {
    getInputSignUpArtist().then(
      ({ displayName, accountId, invitationCode }) => {
        setHiddenDisplayName(!!displayName)
        setHiddenAccountId(!!accountId)
        setHiddenInvitationCode(!!invitationCode)
      }
    )
  }, [])

  return {
    state: {
      hiddenDisplayName,
      hiddenAccountId,
      /**
       * @todo
       * アーティスト登録SNS認証導入時に整理
       */
      hiddenInviteCode: true,
    },
    actions: {
      async onSubmit({
        avatarPath = '',
        displayName: displayNameInput,
        accountId: accountIdInput,
      }) {
        const { displayName, accountId } = await getInputSignUpArtist()

        await artistUpdateArtistProfile({
          variables: {
            inputs: {
              displayName: displayName ?? displayNameInput,
              accountId: accountId ?? accountIdInput,
              introduction: '',
              avatarPath,
            },
          },
        })

        // re-fetch user from auth
        await reloadUser()

        await removeInputSignUpArtist()

        await getUserClaims().then(userClaims => {
          getRedirectAfterLoginPath().then(loginPath => {
            if (userClaims?.orgs?.[config.organizationId]?.artist) {
              router.replace(loginPath.value || routerPages.ConsoleDashboard)
            }
          })
        })
      },
      onClickAvatar() {
        return getImage(config.upload.mimeTypes.image)
          .then(file => {
            if (file.size > config.upload.limit['image']) {
              throw new Error('uploadErrorMaximumSize')
            }
            return upload(file)
          })
          .catch(error => {
            if (error.message === 'uploadErrorMaximumSize') {
              showError(new Error(t('uploadErrorMaximumSize')))
              return
            }
            if (error.message === 'uploadErrorImageOnly') {
              showError(new Error(t('uploadErrorImageOnly')))
              return
            }
            showError(error)
            return
          })
      },
    },
  }
})
